import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ArrowRight, Star, ChevronDown } from 'lucide-react';
import { WHATSAPP_LINK, SITE, PROJECTS } from '../lib/site';

const Hero = () => {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('.hero-reveal-line > span',
        { yPercent: 115 },
        { yPercent: 0, duration: 1, stagger: 0.09 },
      )
      .fromTo('.hero-fade',
        { y: 24, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, stagger: 0.1 },
        '-=0.45',
      )
      .fromTo('.hero-photo-main',
        { clipPath: 'inset(0 0 100% 0)' },
        { clipPath: 'inset(0 0 0% 0)', duration: 1.1, ease: 'power4.inOut' },
        '-=0.8',
      )
      .fromTo('.hero-photo-card',
        { y: 48, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
        '-=0.5',
      );
    }, root);
    return () => ctx.revert();
  }, []);

  const heroProject = PROJECTS[0];

  return (
    <section ref={root} className="relative pt-[112px] md:pt-[144px] overflow-hidden">
      {/* Grid de planos de fondo */}
      <div className="absolute inset-0 blueprint-grid pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-clay/8 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center pb-20 lg:pb-28">
        {/* Columna texto */}
        <div>
          <p className="hero-fade label opacity-0">Construcción · Reformas · Mantenimiento</p>

          <h1 className="mt-7 font-display font-black uppercase tracking-[-0.03em] text-[clamp(2.9rem,7.2vw,6.4rem)] leading-[0.94]">
            <span className="hero-reveal-line block"><span>Reformamos,</span></span>
            <span className="hero-reveal-line block"><span>construimos <em className="font-serifit normal-case font-normal tracking-normal text-clay">tu casa</em></span></span>
            <span className="hero-reveal-line block"><span>en Málaga</span></span>
          </h1>

          <p className="hero-fade opacity-0 mt-7 max-w-[52ch] text-ink-soft text-lg leading-relaxed">
            Empresa de construcción y reformas con <strong className="text-ink font-semibold">+15 años</strong> en la Costa del Sol.
            Presupuesto detallado en 24h, plazos por escrito y equipos propios.
            <em className="font-serifit text-clay"> Sin sorpresas, sin excusas.</em>
          </p>

          <div className="hero-fade opacity-0 mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href={WHATSAPP_LINK('Hola, quiero solicitar un presupuesto para mi reforma o construcción.')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-clay text-white"
              data-size="lg"
            >
              Presupuesto gratis en 24h
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a href="#proyectos" className="btn border border-ink text-ink" data-size="lg">
              Ver proyectos
            </a>
          </div>

          {/* Prueba social */}
          <div className="hero-fade opacity-0 mt-9 flex items-center gap-4">
            <div className="flex -space-x-2.5">
              {['MG', 'JR', 'CL', 'AM'].map((ini, i) => (
                <span
                  key={ini}
                  className="grid place-items-center w-9 h-9 rounded-full border-2 border-paper bg-ink text-paper text-[0.62rem] font-bold"
                  style={{ zIndex: 4 - i, backgroundColor: ['#B65A32', '#16130F', '#8A8378', '#93431F'][i] }}
                >
                  {ini}
                </span>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-0.5" aria-label="Valoración 4.9 sobre 5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} className="fill-clay text-clay" />
                ))}
                <span className="ml-1.5 text-sm font-bold">{SITE.rating}</span>
              </div>
              <p className="text-xs text-ink-faint mt-0.5">+87 reseñas verificadas de clientes en Málaga</p>
            </div>
          </div>

          {/* Stats */}
          <dl className="hero-fade opacity-0 mt-12 grid grid-cols-3 max-w-md divide-x divide-line border-y border-line">
            {[
              { v: '150+', l: 'Proyectos' },
              { v: '15+', l: 'Años' },
              { v: '98%', l: 'Recomiendan' },
            ].map((s) => (
              <div key={s.l} className="px-4 py-4 first:pl-0">
                <dt className="sr-only">{s.l}</dt>
                <dd className="stat-number text-2xl md:text-3xl">{s.v}</dd>
                <dd className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-ink-faint mt-1">{s.l}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Columna foto */}
        <div className="relative">
          <div className="hero-photo-main relative rounded-[4px] overflow-hidden bg-paper-2 shadow-paper aspect-[4/5] max-h-[660px] w-full">
            <img
              src={heroProject.image}
              alt={`${heroProject.title} — reforma realizada por HGO Servicios en ${heroProject.location}`}
              className="w-full h-full object-cover"
              width={1200}
              height={1500}
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/35 via-transparent to-transparent" />
            {/* Caption de revista */}
            <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between gap-4">
              <div>
                <p className="text-[0.62rem] font-bold tracking-[0.22em] uppercase text-paper/80">Proyecto {heroProject.id} · {heroProject.year}</p>
                <p className="text-paper font-display font-bold text-lg mt-1 leading-tight">{heroProject.title}</p>
              </div>
              <span className="text-[0.62rem] font-bold tracking-[0.18em] uppercase text-paper/80 shrink-0">{heroProject.location}</span>
            </div>
          </div>

          {/* Tarjeta flotante — garantía */}
          <div className="hero-photo-card absolute -left-4 md:-left-10 bottom-10 bg-ink text-paper rounded-[4px] px-6 py-5 shadow-[0_24px_60px_-20px_rgba(22,19,15,0.5)] max-w-[240px]">
            <p className="text-clay-light font-display font-black text-3xl tracking-tight">10 años</p>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-paper/70 mt-1 leading-relaxed">
              de garantía por escrito en todos nuestros trabajos
            </p>
          </div>

          {/* Badge circular rotando */}
          <div className="hero-photo-card hidden lg:grid place-items-center absolute -right-6 -top-6 w-28 h-28 rounded-full bg-clay text-paper shadow-[0_16px_40px_-12px_rgba(182,90,50,0.55)]">
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[spin_16s_linear_infinite]">
              <defs>
                <path id="circlePath" d="M 50,50 m -34,0 a 34,34 0 1,1 68,0 a 34,34 0 1,1 -68,0" />
              </defs>
              <text className="fill-paper" style={{ fontSize: '8.2px', letterSpacing: '0.22em', fontFamily: 'Archivo', fontWeight: 700 }}>
                <textPath href="#circlePath">PRESUPUESTO GRATIS · SIN COMPROMISO ·</textPath>
              </text>
            </svg>
            <ArrowRight size={22} className="rotate-[-45deg]" />
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="hidden md:flex justify-center pb-8">
        <div className="scroll-cue">
          <span>Explora</span>
          <span className="scroll-cue-line" />
          <ChevronDown size={14} className="hidden" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
